/**
 * Module-local response handling so src/assistant lifts cleanly into a sister app.
 * Mirrors the host's ProblemDetails error shape.
 */

export interface AssistantProblemDetails {
    status?: number;
    title?: string | null;
    detail?: string | null;
    errors?: {message: string}[];
    [key: string]: unknown;
}

export class AssistantApiError extends Error {
    status: number;
    problemDetails: AssistantProblemDetails | null;

    constructor(
        message: string,
        status: number,
        problemDetails: AssistantProblemDetails | null
    ) {
        super(message);
        this.name = 'AssistantApiError';
        this.status = status;
        this.problemDetails = problemDetails;
    }
}

export const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
        await throwResponseError(response);
    }

    if (response.status === 204) {
        return undefined as T;
    }

    return response.json() as Promise<T>;
};

export const throwResponseError = async (
    response: Response
): Promise<never> => {
    let problemDetails: AssistantProblemDetails | null = null;
    let message = `Request failed (${response.status})`;

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('json')) {
        try {
            problemDetails = (await response.json()) as AssistantProblemDetails;
            message =
                problemDetails.errors?.[0]?.message ??
                problemDetails.detail ??
                problemDetails.title ??
                message;
        } catch {
            // fall through with the default message
        }
    }

    throw new AssistantApiError(message, response.status, problemDetails);
};
